/* eslint-disable react/no-unused-prop-types */
import * as React from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isNil from 'lodash/isNil';

import { navigate } from 'libs/router';
import { askUserToConfirmRouteExitPromise } from 'libs/router/confirm-exit';

import PageSection from 'components/page/page-section/PageSection';
import TabsSection from 'components/tabs/tabs-section/TabsSection';
import NotificationDot from 'components/notification-dot/NotificationDot';
import { withScrollTo } from 'components/ScrollToHOC';

import CcmCampaignOffersList from './list/CcmCampaignOffersList';
import CcmCampaignOffersDetail from './detail/CcmCampaignOffersDetail';

import { fetchCampaignOverview } from 'modules/campaigns/reducers/campaignOverview';
import { uncancelSubcampaign } from 'modules/campaigns/reducers/campaignSubcampaigns';
import { fetchUnreadConversations } from 'modules/messenger/reducers/messengerNotifications';
import { fetchOfferDeliverables } from 'modules/subcampaigns/reducers/offerDeliverables';
import { downloadParticipants, fetchParticipants } from 'modules/campaigns/reducers/campaignParticipants';
import { fetchSubcampaign } from 'modules/subcampaigns/reducers/subcampaign';
import {
  fetchCcmPool,
  flushCcmPool,
  loadCcmAssets,
  updateScreenState
} from 'modules/campaigns/components/ccm/redux/actions';

import { __t, applyTranslations } from 'utils/language';
import { isCanceled, isExpired } from 'utils/subcampaign';
import { getItem, setItem } from 'utils/storage/localStorage';
import { PARTICIPANT_TYPE_AGREED } from 'utils/campaignParticipants';
import {
  CCM_OFFER_TYPES,
  CCM_OFFER_TYPE_TO_STATE_MAP,
  isParticipantTypeSupported
} from './utils/ccm-campaign-offers';

import './ccm-campaign-offers.scss';

applyTranslations(require('./ccm-campaign-offers.i18n.json'));

import type {
  Campaign,
  CcmCampaignOverview,
  Deliverable,
  MessengerNotifications,
  Params,
  Subcampaign,
  SubcampaignWithInfluencerSettings,
  TabsSectionItem
} from 'types';
import type {
  CcmOfferScreenState,
  CcmOfferTab
} from './types';

type ParticipantsParams = {
  activeTab?: string,
  currentPage?: number
};

type OwnProps = {
  params: Params
};

type StateProps = {
  campaignId: number,
  campaign: Campaign | undefined,
  subcampaignId: number | null,
  subcampaign: SubcampaignWithInfluencerSettings,
  deliverables: Array<Deliverable>,
  currentOrganizationId: number,
  ccmOverview: CcmCampaignOverview,
  messengerNotifications: MessengerNotifications,
  activeTab: CcmOfferTab,
  poolScreenState: CcmOfferScreenState,
  ccmLegalRequirementsTemplate: string | null,
  outfitGuidelineLink: string,
  affiliationPerClickCost: string
};

type DispatchProps = {
  dispatchFetchCampaignOverview: (campaignId: number) => void,
  dispatchFetchParticipants: (campaignId: number, params: ParticipantsParams) => Promise<void>,
  dispatchFlushPool: () => void,
  dispatchFetchUnreadConversations: (organizationId: number) => Promise<void>,
  dispatchFetchOfferDeliverables: (offerId: number) => Promise<Array<Deliverable>>,
  dispatchUpdateScreenState: (screenState: CcmOfferScreenState) => void,
  dispatchFetchSubcampaign: (subcampaignId: number) => Promise<SubcampaignWithInfluencerSettings>,
  dispatchDownloadParticipants: (campaignId: number, campaignName: string, participantType: string) => Promise<void>,
  dispatchRemoveFromRevoked: (subcampaign: Subcampaign) => Promise<void>,
  dispatchAskUserToConfirmRouteExit: () => Promise<'next' | 'cancel'>,
  dispatchLoadCcmAssets: () => Promise<void>
};

type Props = OwnProps & StateProps & DispatchProps;

const MSG_KEY = 'ccm_campaign_offers';

const getSelectedTab = (
  params: Params
): CcmOfferTab => {
  const localStorageKey = 'savedCcmOfferTab';
  const savedTab = getItem(localStorageKey);
  const activeTabFromUrl = params?.participantType ?? null;

  // using local storage allows us to keep the selected tab when viewing a specific offer
  if (!isNil(activeTabFromUrl) && isParticipantTypeSupported(activeTabFromUrl)) {
    setItem(localStorageKey, activeTabFromUrl);
    return activeTabFromUrl as CcmOfferTab;
  }

  if (isNil(activeTabFromUrl) && isNil(savedTab)) {
    setItem(localStorageKey, CCM_OFFER_TYPES[0]);
    return CCM_OFFER_TYPES[0] as CcmOfferTab;
  }

  if (!isNil(savedTab)) {
    return savedTab as CcmOfferTab;
  }

  return CCM_OFFER_TYPES[0] as CcmOfferTab;
};

const OffersDetailWithScrollTo = withScrollTo(CcmCampaignOffersDetail, {
  suppressReplaceState: true
});

const CcmCampaignOffers: React.FC<Props> = ({
  campaignId,
  campaign,
  subcampaignId,
  subcampaign,
  deliverables,
  currentOrganizationId,
  ccmOverview,
  activeTab,
  poolScreenState,
  messengerNotifications,
  ccmLegalRequirementsTemplate,
  outfitGuidelineLink,
  affiliationPerClickCost,
  dispatchFetchCampaignOverview,
  dispatchFetchParticipants,
  dispatchFlushPool,
  dispatchFetchUnreadConversations,
  dispatchFetchOfferDeliverables,
  dispatchUpdateScreenState,
  dispatchFetchSubcampaign,
  dispatchDownloadParticipants,
  dispatchRemoveFromRevoked,
  dispatchAskUserToConfirmRouteExit,
  dispatchLoadCcmAssets
}): JSX.Element => {
  const getNofiticationCount = (
    conversationExternalIdList: Array<string>
  ): number => (
    conversationExternalIdList.reduce((sum, externalId) => {
      if (messengerNotifications.unreadChatsMap[externalId]) {
        return sum + 1;
      }

      return sum;
    }, 0)
  );

  const getTabs = (): Array<TabsSectionItem> => (
    CCM_OFFER_TYPES.map(type => {
      const overviewGroup = get(ccmOverview, CCM_OFFER_TYPE_TO_STATE_MAP[type].overviewName, null);
      const creatorCount = overviewGroup?.creators ?? 0;
      const conversationExternalIdList = overviewGroup?.conversationExternalIdList ?? [];
      const notificationsCount = getNofiticationCount(conversationExternalIdList);

      return ({
        identifier: type,
        label: (
          <div className="ccm-campaign-offers__tab">
            <div className="ccm-campaign-offers__tab-text">
              {__t(`${MSG_KEY}.tabs.${type}`)} ({creatorCount})
            </div>
            {
              notificationsCount > 0 && (
                <div className="ccm-campaign-offers__tab-notification">
                  <NotificationDot />
                </div>
              )
            }
          </div>
        )
      });
    })
  );

  const handleDownloadParticipants = (): void => {
    dispatchDownloadParticipants(
      campaignId,
      campaign?.name ?? __t(`${MSG_KEY}.filenames.placeholder`),
      PARTICIPANT_TYPE_AGREED
    );
  };

  const handlePageChange = (
    page: number
  ): void => {
    dispatchUpdateScreenState({
      ...poolScreenState,
      page
    });
  };

  const handleTabClick = async (
    identifier?: string | null
  ): Promise<void> => {
    const status: string = await dispatchAskUserToConfirmRouteExit();

    if (status === 'cancel') {
      return;
    }

    dispatchFetchCampaignOverview(campaignId);

    navigate.to(`/campaigns/ccm/${campaignId}/participants/${identifier || CCM_OFFER_TYPES[0]}?clicked=tab`);
  };

  const handleRowClick = (
    subcampaignIdRow: number
  ): void => {
    dispatchFetchSubcampaign(subcampaignIdRow)
      .then(fetchedSubcampaign => dispatchFetchOfferDeliverables(fetchedSubcampaign.offer.id));

    navigate.to(`/campaigns/ccm/${campaignId}/subcampaigns/${subcampaignIdRow}#offer-${subcampaignIdRow}`);
  };

  const handleRemoveFromRevoked = async (
    revokedSubcampaign: Subcampaign | null | undefined
  ): Promise<void> => {
    if (isNil(revokedSubcampaign)) {
      return;
    }

    if (isCanceled(revokedSubcampaign) || isExpired(revokedSubcampaign)) {
      await dispatchRemoveFromRevoked(revokedSubcampaign)
        .then(() => dispatchFetchParticipants(campaignId, { activeTab: activeTab as string }))
        .then(() => navigate.to(`/campaigns/ccm/${campaignId}/participants/revoked?clicked=tab`));
    }
  };

  React.useEffect(() => {
    const updatedActiveTab = activeTab === 'accepted' ? 'agreed' : activeTab;
    dispatchFetchParticipants(campaignId, {
      activeTab: updatedActiveTab as string,
      currentPage: poolScreenState.page
    });

    return () => {
      dispatchFlushPool();
    };
  }, [campaignId, activeTab, poolScreenState.page]);

  React.useEffect(() => {
    if (!isNil(subcampaignId)) {
      dispatchFetchSubcampaign(subcampaignId)
        .then(fetchedSubcampaign => dispatchFetchOfferDeliverables(fetchedSubcampaign.offer.id));
    }
  }, []);

  React.useEffect(() => {
    dispatchFetchUnreadConversations(currentOrganizationId);
  }, [currentOrganizationId]);

  React.useEffect(() => {
    dispatchLoadCcmAssets();
  }, []);

  return (
    <div className="ccm-campaign-offers">
      <PageSection
        padding="none"
        style="none"
        title={__t(`${MSG_KEY}.title`)}
      >
        <div className="ccm-campaign-offers__tabs">
          <TabsSection
            withShadow
            clickableActiveTab
            items={getTabs()}
            activeTab={activeTab}
            onClick={handleTabClick}
          />
        </div>
      </PageSection>
      <div className="ccm-campaign-offers__info">
        <CcmCampaignOffersList
          campaignId={campaignId}
          subcampaignId={subcampaignId}
          ccmOverview={ccmOverview}
          activeTab={activeTab}
          poolScreenState={poolScreenState}
          unreadChatsMap={messengerNotifications.unreadChatsMap}
          onPageChange={handlePageChange}
          onRowClicked={handleRowClick}
          onRemoveFromRevoked={handleRemoveFromRevoked}
          onDownloadParticipants={handleDownloadParticipants}
        />
        {
          subcampaignId && (
            <OffersDetailWithScrollTo
              hash={`offer-${subcampaignId}`}
              campaignId={campaignId}
              campaign={campaign}
              subcampaign={subcampaign}
              deliverables={deliverables}
              activeTab={activeTab}
              runtimeLegalRequirementsTemplate={ccmLegalRequirementsTemplate}
              outfitGuidelineLink={outfitGuidelineLink}
              affiliationPerClickCost={affiliationPerClickCost}
            />
          )
        }
      </div>
    </div>
  );
};

const mapStateToProps = ({
  campaignOverview,
  ccmCampaign,
  messengerNotifications,
  offerDeliverables,
  organizations,
  campaignBrief,
  subcampaign,
  runtimeConfig
}, { params }): StateProps => ({
  campaignId: Number(params.id),
  campaign: campaignBrief.brief,
  subcampaignId: !isNil(params.subcampaignId) ? Number(params.subcampaignId) : null,
  subcampaign: subcampaign.data,
  currentOrganizationId: organizations.currentOrganizationId,
  ccmOverview: campaignOverview.ccmOverview,
  messengerNotifications,
  activeTab: getSelectedTab(params),
  poolScreenState: ccmCampaign.screenState,
  ccmLegalRequirementsTemplate: ccmCampaign?.ccmLegalRequirementsTemplate ?? '',
  deliverables: (subcampaign.data?.offer && offerDeliverables.deliverablesByOfferId[subcampaign.data.offer.id]) || [],
  outfitGuidelineLink: runtimeConfig.config.outfitGuidelineLink,
  affiliationPerClickCost: runtimeConfig.config.affiliationPerClickCost
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCampaignOverview: (campaignId: number) => (
    dispatch(fetchCampaignOverview(campaignId, 'ccm'))
  ),
  dispatchFetchParticipants: (campaignId: number, params: ParticipantsParams) => (
    params.activeTab === 'pool'
      ? dispatch(fetchCcmPool(campaignId, params.currentPage))
      : dispatch(fetchParticipants(
        campaignId,
        params.activeTab as string,
        null, {
          includeDeliverablesOverview: true
        }
      ))
  ),
  dispatchFlushPool: () => (
    dispatch(flushCcmPool())
  ),
  dispatchFetchUnreadConversations: (organizationId: number) => (
    dispatch(fetchUnreadConversations(organizationId))
  ),
  dispatchFetchOfferDeliverables: (offerId: number) => (
    dispatch(fetchOfferDeliverables(offerId, true))
  ),
  dispatchUpdateScreenState: (screenState: CcmOfferScreenState) => (
    dispatch(updateScreenState(screenState))
  ),
  dispatchFetchSubcampaign: (subcampaignId: number) => (
    dispatch(fetchSubcampaign(subcampaignId, { includeDetails: true, showLoading: false }))
  ),
  dispatchDownloadParticipants: (campaignId: number, campaignName: string, type: string) => (
    dispatch(downloadParticipants(campaignId, campaignName, type, '', { includeDeliverablesOverview: true }))
  ),
  dispatchRemoveFromRevoked: (subcampaign: Subcampaign | null | undefined) => (
    dispatch(uncancelSubcampaign(subcampaign, 'ccm'))
  ),
  dispatchAskUserToConfirmRouteExit: () => (
    askUserToConfirmRouteExitPromise(dispatch)
  ),
  dispatchLoadCcmAssets: () => dispatch(loadCcmAssets())
});

export default connect(mapStateToProps, mapDispatchToProps)(CcmCampaignOffers);
